/**
 * 「初日から稼げる」の画面の日本語の文言（en.playEarn.ts と同じキーを全部持つ = 型で強制）。言い回しの決め事は en.playEarn.ts の冒頭。
 * 日本語は既定のビルドでは出さない（VITE_KUMO_LANGUAGES・2026-10-05）。出すときに読める日本語にしておく。
 */
import type { enPlayEarn } from './en.playEarn';

type PlayEarnMessages = { [K in keyof typeof enPlayEarn]: string };

export const jaPlayEarn: PlayEarnMessages = {
  'pe.nav': 'プレイの稼ぎ',
  'pe.title': '稼ぎ',
  'pe.thisWeek': '今週 · {range}（UTC）',
  'pe.paused': '今は数えを止めています。数え終わったプレイの分は払います。',
  'pe.pool': '{product} の今週の広告の予算',
  'pe.poolUsed': 'これまでに出る見込み {amount}',
  'pe.rate': '今週の 1 プレイの額',
  'pe.rateValue': '1 プレイ {rate}',
  'pe.rateFull': '全体のプレイが {plays} までは満額。それより多く遊ばれたら、{pool} をプレイ数で山分けします。',
  'pe.rateSplit': '1 プレイ {full} で {pool} を超えるほど遊ばれたので、プレイ数で山分けしています。週の終わりまで変わることがあります。',
  'pe.estimate': '今週の見込み',
  'pe.estimateMath': '{counted} − {deducted} = {net} × {rate}',
  'pe.estimateMathPlain': '{net} × {rate}',
  'pe.estimateMathReview': '{counted} − {deducted} − 確認中 {review} = {net} × {rate}',
  'pe.estimateCapped': '1 人の作り手は 1 週間 {cap} までです。',
  'pe.counted': '数えたプレイ',
  'pe.countedHint': '{product} の広告が出たページで、1 人・1 ゲーム・1 日に 1 回',
  'pe.notCounted': '数えないプレイ',
  'pe.notCountedHint': 'ご自身のプレイ・同じ人の同じゲームの 1 日 2 回目から・自動のアクセス',
  'pe.deducted': '減点',
  'pe.deductedHint': '疑わしいプレイ 1 回につき、正しいプレイ 1 回を引きます',
  'pe.byGame': 'ゲームごと',
  'pe.col.game': 'ゲーム',
  'pe.col.plays': 'プレイ',
  'pe.col.est': '見込み',
  'pe.col.status': '状態',
  'pe.status.ok': 'OK',
  'pe.status.review': '確認中',
  'pe.reviewNote': '確認中のゲームは、プレイを確かめる間、その週は 0 円です。',
  'pe.noGames': '今週はまだ数えたプレイがありません。',
  'pe.onHold': '保留中（{days} 日の確認）',
  'pe.onHoldHint': '今週の分は {day}（UTC）の締めでここに入ります。',
  'pe.ready': '送る準備中',
  'pe.readyHint': '確認が終わりました。Amazon のギフトカードでお送りします。',
  'pe.sent': 'これまでに送った額',
  'pe.payLine': '{days} 日の確かめが済んだら、アカウントのメール（{email}）あてに Amazon のギフトカードでお送りします。',
  'pe.country.label': 'ギフトカードの国',
  'pe.country.choose': 'ギフトカードを受け取る Amazon の国を選んでください',
  'pe.country.placeholder': '国を選ぶ',
  'pe.country.hint': 'ギフトカードは {store} で買います。そのお店でだけ使えます。',
  'pe.country.saving': '保存しています…',
  'pe.country.saved': '保存しました',
  'pe.weeks': 'これまでの週',
  'pe.col.week': '週',
  'pe.col.amount': '額',
  'pe.week.held': '{day} まで保留',
  'pe.week.ready': '送る準備中',
  'pe.week.sent': '{day} に送りました',
  'pe.week.voided': '払いなし',
  'pe.empty.title': '最初のプレイを待っています',
  'pe.empty.body': '公開した日からプレイを数えます。{product} の広告が出るゲームで、1 人・1 ゲーム・1 日に 1 回です。',
  'pe.foot': 'ゲームに出る広告は {product} 自身の広告です。{product} が広告の枠の代金を払います。お問い合わせ: {email}',
  'pe.off.title': 'まだ使えません',
  'pe.off.body': 'このアカウントでは、プレイで稼ぐ機能はまだ開いていません。',
  // 広告のカードの試しの作り手の版（en.playEarn.ts の pe.card.* と同じ・告知の文 §3・§4 の日本語の言い方）
  'pe.card.rate': '{n} セント',
  'pe.card.body': '広告を ON にすると、最初の 1 プレイから 1 プレイ {rate}。出る広告は {product} 自身の広告で、{product} が公開中のページに入れます（コードは不要）。',
  'pe.card.on': '1 プレイ {rate}。1 週間の様子は{link}で見られます。',
  'pe.card.play': '1 人・1 ゲーム・1 日に 1 回まで。機械と自分のプレイは数えません。',
  'pe.card.pot': '今週の入れ物は {pool}。使い切ったらプレイ数で山分けします。作り手 1 人は週 {cap} まで。',
  'pe.card.paid': '週が終わってから {days} 日確かめて、アカウントのメールに Amazon のギフトカードで送ります。',
  'pe.card.more': '詳しくは{link}。',
  'pe.card.amazon': 'Amazon はスポンサーではありません。',
  'pe.card.houseNote': 'いまあなたのアプリに出る広告は、{product} の短い宣伝だけです。数えるのは {product} の広告が出たページのプレイです。どこに出すかはあなたが選べます。'
};
